<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

  @if (dispatch) {
    <app-page-detail-hero [auditStamp]="dispatch"
      icon="pi pi-send"
      [eyebrow]="isManualGoodsOut() ? 'Goods Delivery Note' : 'Warehousing Dispatch'"
      [title]="dispatch.dispatchNumber || (isManualGoodsOut() ? 'Goods Out' : 'Dispatch')"
      [subtitle]="clientName(dispatch.crmClientId) + ' | ' + sourceSummary()">
      <div tags>
        <p-tag [value]="dispatch.status" [severity]="statusSeverity(dispatch.status)"></p-tag>
        @if (advancePlanningLabel()) {
          <p-tag [value]="advancePlanningLabel()" [severity]="isGateOpen() ? 'success' : 'info'"></p-tag>
        }
        @if (showLifecycleTag()) {
          <p-tag [value]="lifecycleStatus()" severity="secondary"></p-tag>
        }
        <p-tag [value]="dispatch.sourceType || 'MANUAL'" severity="info"></p-tag>
        <p-tag [value]="displayDate(dispatch.scheduledDate)" severity="secondary"></p-tag>
        @if (hasInheritedReservationHint()) {
          <p-tag value="Inherited Reservations" severity="warn"></p-tag>
        }
      </div>
      <div actions>
        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" [routerLink]="backLink()" queryParamsHandling="preserve"></p-button>
        @if (isManualGoodsOut()) {
          <p-button label="View Movements" icon="pi pi-history" severity="secondary" [outlined]="true"
            routerLink="/warehousing/stock-ledger"
            [queryParams]="{ referenceType: 'MANUAL_GOODS_OUT', referenceNumber: dispatch.dispatchNumber, movementFrom: dispatch.dispatchDate, movementTo: dispatch.dispatchDate }"></p-button>
          <p-button label="Download GDN" icon="pi pi-file-pdf" [loading]="isDownloadingNote" [disabled]="lifecycleStatus() !== 'DISPATCHED' || isDownloadingNote" (click)="downloadGoodsOutNote()"></p-button>
        } @else {
        @if (dispatch.crossDockOrchestrationId) {
          <p-button label="Orchestration" icon="fa-solid fa-network-wired" severity="secondary" [outlined]="true" [routerLink]="['/transport-planning/orchestrations', dispatch.crossDockOrchestrationId]"></p-button>
        }
        @if (!usesVirtualWarehouse()) {
          <p-button label="Open Scan Workflow" icon="pi pi-barcode" severity="secondary" [outlined]="true" [disabled]="!isGateOpen()" [routerLink]="['/warehousing/dispatch/view', dispatch.id, 'pick-scan']" queryParamsHandling="preserve"></p-button>
          <p-button label="Print Unit Labels" icon="pi pi-print" severity="secondary" [outlined]="true" [disabled]="isPrintingLabels" (click)="printReservedUnitLabels()"></p-button>
        }
        @if (canReserve()) {
          <p-button label="Reserve Stock" icon="pi pi-lock" severity="warn" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="reserveDispatch()"></p-button>
        }
        <app-warehousing-supply-reserve documentType="DISPATCH" [documentId]="dispatch.id!"
          [items]="items" [zones]="masterData.ZONE" [conditions]="masterData.ITEM_CONDITION"
          [canEdit]="canChooseSupply()" [autoOpen]="supplyAutoOpen()"
          (changed)="loadDispatch()"></app-warehousing-supply-reserve>
        }
      </div>
    </app-page-detail-hero>

    @if (isManualGoodsOut()) {
      <app-page-form-section icon="pi pi-file" title="Goods Out Note" subtitle="Confirmed stock movement from one warehouse.">
        <div class="warehousing-detail-list">
          <div><span>Stock Owner</span><strong>{{ clientName(dispatch.crmClientId) }}</strong></div>
          <div><span>Account</span><strong>{{ accountName(dispatch.crmAccountId) }}</strong></div>
          <div><span>Warehouse</span><strong>{{ goodsOutWarehouse() }}</strong></div>
          <div><span>Posted Date</span><strong>{{ displayDate(dispatch.dispatchDate) }}</strong></div>
          <div><span>Customer Reference</span><strong>{{ dispatch.referenceNumber || '-' }}</strong></div>
          <div><span>Delivery Address</span><strong>{{ dispatch.deliveryAddress || '-' }}</strong></div>
        </div>
        @if (dispatch.notes) { <p class="mt-3">{{ dispatch.notes }}</p> }
      </app-page-form-section>
      <app-page-data-panel title="Issued Items" [resultText]="dispatch.lines.length + ' lines'">
        <p-table [value]="dispatch.lines" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table">
          <ng-template pTemplate="header"><tr><th>Item</th><th>Zone</th><th>Condition</th><th>Issued Units</th><th class="text-right">Quantity Out</th></tr></ng-template>
          <ng-template pTemplate="body" let-line><tr>
            <td>{{ itemName(line.itemId) }}</td>
            <td>{{ masterName('ZONE', line.zoneId) }}</td>
            <td>{{ masterName('ITEM_CONDITION', line.conditionId) }}</td>
            <td>{{ reservationCodes(line) }}</td>
            <td class="text-right">{{ line.quantityExited | number:'1.0-4' }}</td>
          </tr></ng-template>
        </p-table>
      </app-page-data-panel>
    } @else {

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>
    <app-warehousing-desktop-outbound [dispatch]="dispatch"
      [disabledReason]="usesVirtualWarehouse() ? 'Physical stock-ledger warehouse required.' : ''"
      (changed)="loadDispatch()"></app-warehousing-desktop-outbound>
    <app-warehousing-work-history documentType="DISPATCH" [documentId]="dispatch.id"></app-warehousing-work-history>
    @if (hasInheritedReservationHint()) {
      <app-page-form-section icon="pi pi-link" title="Reservation Inheritance" subtitle="This dispatch is bound to exact stock units that were reserved upstream in warehousing.">
        <p class="warehousing-detail-note">Inherited reservations stay linked to their upstream allocation record. Units marked <strong>UPSTREAM LOCKED</strong> are still held by the original warehouse reservation, while <strong>DISPATCH LOCKED</strong> units were re-locked by dispatch at execution time.</p>
      </app-page-form-section>
    }

    <div class="warehousing-detail-grid">
      <app-page-form-section icon="pi pi-building" title="Dispatch Header" subtitle="Customer, source, and schedule information.">
        <div class="warehousing-detail-list">
          <div><span>Client</span><strong>{{ clientName(dispatch.crmClientId) }}</strong></div>
          <div><span>Account</span><strong>{{ accountName(dispatch.crmAccountId) }}</strong></div>
          <div><span>Source Type</span><strong>{{ dispatch.sourceType || 'MANUAL' }}</strong></div>
          <div><span>Source Document</span><strong>{{ dispatch.sourceNumber || '-' }}</strong></div>
          <div><span>External Reference</span><strong>{{ dispatch.referenceNumber || '-' }}</strong></div>
          <div><span>Scheduled</span><strong>{{ displayDate(dispatch.scheduledDate) }}</strong></div>
          <div><span>Dispatch Date</span><strong>{{ displayDate(dispatch.dispatchDate) }}</strong></div>
          <div><span>Delivery Address</span><strong>{{ dispatch.deliveryAddress || '-' }}</strong></div>
        </div>
      </app-page-form-section>

      <app-page-form-section icon="pi pi-truck" title="Transport Linkage" subtitle="Linked transport release, shipment, and mirrored delivery visibility.">
        <div class="warehousing-detail-list">
          <div><span>Lifecycle</span><strong>{{ lifecycleStatus() }}</strong></div>
          <div><span>Transport Release</span><strong>{{ dispatch.transportReleaseNumber || '-' }}</strong></div>
          <div><span>Shipment</span><strong>{{ dispatch.shipmentNumber || '-' }}</strong></div>
          <div><span>Shipment Status</span><strong>{{ dispatch.shipmentStatus || '-' }}</strong></div>
          <div><span>Delivery</span><strong>{{ dispatch.deliveryNumber || '-' }}</strong></div>
          <div><span>Delivery Status</span><strong>{{ dispatch.deliveryStatus || '-' }}</strong></div>
          <div>
            <span>Cross-Dock Orchestration</span>
            <strong>
              @if (dispatch.crossDockOrchestrationId) {
                <a [routerLink]="['/transport-planning/orchestrations', dispatch.crossDockOrchestrationId]">
                  {{ dispatch.crossDockOrchestrationNumber || ('#' + dispatch.crossDockOrchestrationId) }}
                </a>
              } @else {
                -
              }
            </strong>
          </div>
          <div><span>Gated By Transfer</span><strong>{{ dispatch.gatedByTransferId || '-' }}</strong></div>
          <div><span>Receipt Gate</span><strong><p-tag [value]="gateStatusLabel()" [severity]="gateStatusSeverity()"></p-tag></strong></div>
          @if (advancePlanningLabel()) {
            <div><span>Transport Planning</span><strong>{{ advancePlanningLabel() }}</strong></div>
          }
        </div>
      </app-page-form-section>
    </div>

    <app-page-form-section icon="pi pi-align-left" title="Notes" subtitle="Internal dispatch instructions.">
      <p class="warehousing-detail-note">{{ dispatch.notes || 'No notes provided.' }}</p>
    </app-page-form-section>

    <app-page-data-panel title="Dispatch Lines" subtitle="Requested quantities, source rows, reservation scan progress, and exact stock units." [resultText]="dispatch.lines.length + ' records'">
      <p-table [value]="dispatch.lines || []" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '64rem' }">
        <ng-template pTemplate="header"><tr><th>Item / Source</th><th>Warehouse</th><th>Zone</th><th>Condition</th><th>Reserved Units</th><th>Binding</th><th>Work Progress</th><th class="text-right">Requested</th><th class="text-right">Reserved</th><th class="text-right">Exited</th></tr></ng-template>
        <ng-template pTemplate="body" let-line>
          <tr>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ itemName(line.itemId) }}</span>
                <span class="cell-meta"><i class="pi pi-link"></i> {{ lineSource(line) }}</span>
              </div>
            </td>
            <td>{{ masterName('WAREHOUSE', line.warehouseId) }}</td>
            <td>{{ masterName('ZONE', line.zoneId) }}</td>
            <td>{{ masterName('ITEM_CONDITION', line.conditionId) }}</td>
            <td>{{ reservationCodes(line) }}</td>
            <td>{{ reservationBindingLabel(line.reservations?.[0]) }}</td>
            <td>{{ scanSummary(line) }}</td>
            <td class="text-right">{{ line.quantityRequested | number:'1.0-2' }}</td>
            <td class="text-right">{{ line.quantityReserved || 0 | number:'1.0-2' }}</td>
            <td class="text-right">{{ line.quantityExited || 0 | number:'1.0-2' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage"><tr><td colspan="10"><div class="warehousing-empty-state"><i class="pi pi-search"></i><strong>No dispatch lines</strong><span>No item lines were added to this dispatch.</span></div></td></tr></ng-template>
      </p-table>
    </app-page-data-panel>

    <app-page-data-panel title="Reservation Scan Ledger" subtitle="Unit-level pick, stage, and load timestamps." [resultText]="dispatch.lines.length + ' lines'">
      <p-table [value]="reservationAuditRows()" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '72rem' }">
        <ng-template pTemplate="header"><tr><th>Item</th><th>Stock Unit</th><th>Unit Location</th><th>Binding</th><th>Provenance</th><th>Status</th><th>Picked</th><th>Staged</th><th>Loaded</th></tr></ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ itemName(row.itemId) }}</td>
            <td>{{ row.reservation.stockUnitCode || ('Unit #' + row.reservation.stockUnitId) }}</td>
            <td>{{ reservationLocationLabel(row.reservation) }}</td>
            <td>{{ reservationBindingLabel(row.reservation) }}</td>
            <td>{{ reservationSourceLabel(row.reservation) }}</td>
            <td><p-tag [value]="row.reservation.scanStatus || 'PENDING'" [severity]="statusSeverity(row.reservation.scanStatus || 'PENDING')"></p-tag></td>
            <td>{{ displayDateTime(row.reservation.pickedAt) }}</td>
            <td>{{ displayDateTime(row.reservation.stagedAt) }}</td>
            <td>{{ displayDateTime(row.reservation.loadedAt) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage"><tr><td colspan="9"><div class="warehousing-empty-state"><i class="pi pi-search"></i><strong>No reserved units</strong><span>Reserve stock to start scan execution.</span></div></td></tr></ng-template>
      </p-table>
    </app-page-data-panel>
    <app-field-resources-panel
      *ngIf="dispatch.id"
      targetType="WAREHOUSE_DISPATCH"
      [targetId]="dispatch.id"
      [targetReference]="dispatch.dispatchNumber"
      [customerCrmClientId]="dispatch.crmClientId"
      [locationName]="dispatch.deliveryAddress">
    </app-field-resources-panel>
    }
  } @else if (!isLoading) {
    <div class="warehousing-empty-detail">
      <i class="pi pi-search"></i>
      <strong>Dispatch not found</strong>
      <span>The selected dispatch could not be loaded.</span>
    </div>
  }
</app-page-shell>
